import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { Archive, ArchiveRestore, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Organization } from '@/types';
import { CapitolIcon } from '@/icons/capitol-icon';
import { Card, CardContent } from '@/components/ui/card';
import { ROUTES } from '@/constants/routes';
import { cn } from '@/lib/utils';

interface OrganizationItemProps {
  org: Organization;
  handleOpenMemberModal: (id: string) => void;
  handleToggleArchive: (org: Organization) => void;
  isArchiving: boolean;
}

export const OrganizationItem = ({
  org,
  handleOpenMemberModal,
  handleToggleArchive,
  isArchiving,
}: OrganizationItemProps) => {
  const navigate = useOrgNavigate();
  const { id, name, imageUrl, hasSso, archived } = org;

  return (
    <Card className={cn(archived && 'opacity-60')}>
      <CardContent className='flex justify-between items-center'>
        <div className='flex gap-3 items-center'>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt='Org logo'
              className='h-10 w-10 object-cover rounded-md shrink-0'
            />
          ) : (
            <div className='size-10'>
              <CapitolIcon />
            </div>
          )}
          <div className='flex flex-col'>
            <div className='font-semibold'>{name}</div>
            <div className='text-xs text-muted-foreground font-mono'>{id}</div>
          </div>
          {hasSso && (
            <span className='ml-2 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs text-green-500 border-green-500/30'>
              <Shield className='size-3' />
              SSO
            </span>
          )}
          {archived && (
            <span className='ml-2 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs text-muted-foreground border-muted-foreground/30'>
              <Archive className='size-3' />
              Archived
            </span>
          )}
        </div>
        <div className='flex gap-2'>
          <Button
            variant='secondary'
            onClick={() => {
              handleOpenMemberModal(id);
            }}
          >
            Add member
          </Button>
          <Button
            variant='outline'
            onClick={() => navigate(`${ROUTES.ADMIN.ORGS}/${id}`)}
          >
            Manage
          </Button>
          <Button
            variant='outline'
            disabled={isArchiving}
            onClick={() => handleToggleArchive(org)}
          >
            {archived ? (
              <>
                <ArchiveRestore className='size-4' />
                Unarchive
              </>
            ) : (
              <>
                <Archive className='size-4' />
                Archive
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
